/**
 * StandaloneChair: a first-class placeable chair object for ceremony-style
 * and chair-only arrangements. Visually matches the chairs rendered around
 * tables so the floor plan reads consistently.
 *
 * GEOMETRY NOTE: catalog defaultWidthFt = 1.5ft. The visual is a circle
 * whose RADIUS = defaultWidthFt * FEET_TO_PX / 2 = 0.75ft * 12px = 9px.
 * NOT 1.5ft radius (which would be 18px and visually too large).
 * This is the same pattern as the chair circles on RoundTable.tsx.
 *
 * x/y stored at CENTER (same convention as all non-dance-floor objects).
 */

import { Circle, Group, Text } from 'react-konva';
import type Konva from 'konva';
import type { LayoutObject, ObjectCatalogEntry } from '../../../domain/types';
import { FEET_TO_PX } from '../canvasConstants';

interface StandaloneChairProps {
  object: LayoutObject;
  catalogEntry: ObjectCatalogEntry;
  isSelected: boolean;
  onSelect: (e?: Konva.KonvaEventObject<Event>) => void;
  onDragEnd: (x: number, y: number) => void;
  roomRotationDegrees: number;
}

export function StandaloneChair({ object, catalogEntry, isSelected, onSelect, onDragEnd }: StandaloneChairProps) {
  // radius = half the catalog width in pixels (not the full width)
  const radius = (catalogEntry.defaultWidthFt * FEET_TO_PX) / 2;

  function handleDragEnd(e: Konva.KonvaEventObject<DragEvent>) {
    const node = e.target;
    onDragEnd(node.x() / FEET_TO_PX, node.y() / FEET_TO_PX);
  }

  return (
    <Group
      id={object.id}
      x={object.x * FEET_TO_PX}
      y={object.y * FEET_TO_PX}
      rotation={object.rotation}
      draggable={!object.locked}
      onClick={onSelect}
      onTap={onSelect}
      onDragEnd={handleDragEnd}
      opacity={object.locked ? 0.85 : 1}
      onMouseEnter={(e) => {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = object.locked ? 'default' : 'grab';
      }}
      onMouseLeave={(e) => {
        const c = e.target.getStage()?.container();
        if (c) c.style.cursor = 'default';
      }}
    >
      <Circle
        radius={radius}
        fill="#FBF8F2"
        stroke={isSelected ? '#C9A35C' : '#C9A35C'}
        strokeWidth={isSelected ? 2.5 : 1}
      />
      {object.locked && (
        <Text text={'\u{1F512}'} fontSize={9} x={0} y={-(radius + 8)} offsetX={4.5} offsetY={4.5} listening={false} />
      )}
    </Group>
  );
}
